import { Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { UpdateContractPayload } from './types';
import type { ContractStatus } from './ContractStatusBadge';
import { getContractTypeSchema, CONTRACT_TYPE_OPTIONS } from './contractTypeSchema';

interface ContractMetadataFormProps {
  form: UpdateContractPayload;
  isSaving: boolean;
  saveError: string | null;
  contractStatus: ContractStatus;
  onChange: (form: UpdateContractPayload) => void;
  onSave: () => void;
  onCancel: () => void;
}

const STATUS_OPTIONS: ContractStatus[] = [
  'draft', 'under_review', 'approved', 'active', 'expiring', 'expired', 'terminated', 'archived',
];
const CURRENCY_OPTIONS = ['EUR', 'USD', 'GBP', 'CHF', 'SEK', 'NOK', 'DKK'];

const IC = 'w-full h-8 rounded border border-input bg-background px-2.5 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const LB = 'text-xs font-medium text-muted-foreground block mb-1';

export function ContractMetadataForm({
  form, isSaving, saveError, contractStatus, onChange, onSave, onCancel,
}: ContractMetadataFormProps) {
  const u = (patch: Partial<UpdateContractPayload>) => { onChange({ ...form, ...patch }); };
  const schema = getContractTypeSchema(form.contract_type);

  // ISO date strings (YYYY-MM-DD) compare correctly as strings.
  const dateError =
    form.start_date && form.end_date && form.end_date < form.start_date
      ? 'End date must be on or after the start date.'
      : null;

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (!dateError) onSave(); }}
      className="space-y-3"
      aria-label="Edit contract metadata"
    >
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor="meta-title" className={LB}>Title <span aria-hidden="true">*</span></label>
          <input id="meta-title" className={IC} required maxLength={500}
            value={form.title ?? ''} onChange={(e) => { u({ title: e.target.value }); }} aria-required="true" />
        </div>
        <div>
          <label htmlFor="meta-type" className={LB}>Contract Type</label>
          <select id="meta-type" className={IC} value={form.contract_type ?? 'other'}
            onChange={(e) => { u({ contract_type: e.target.value }); }}>
            {CONTRACT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor="meta-vendor" className={LB}>{schema.vendorLabel}</label>
          <input id="meta-vendor" className={IC} value={form.vendor_name ?? ''}
            onChange={(e) => { u({ vendor_name: e.target.value }); }} />
        </div>
        <div>
          <label htmlFor="meta-customer" className={LB}>{schema.customerLabel}</label>
          <input id="meta-customer" className={IC} value={form.customer_name ?? ''}
            onChange={(e) => { u({ customer_name: e.target.value }); }} />
        </div>
      </div>
      {schema.showValue && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="meta-value" className={LB}>{schema.valueLabel}</label>
            <input id="meta-value" className={IC} inputMode="decimal" placeholder="0.00"
              value={form.contract_value ?? ''} onChange={(e) => { u({ contract_value: e.target.value }); }}
              aria-describedby="meta-value-hint" />
            <span id="meta-value-hint" className="sr-only">Enter as decimal string e.g. 125000.00</span>
          </div>
          <div>
            <label htmlFor="meta-currency" className={LB}>Currency</label>
            <select id="meta-currency" className={IC} value={form.currency ?? 'EUR'}
              onChange={(e) => { u({ currency: e.target.value }); }}>
              {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      )}
      {schema.showTerm && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="meta-start" className={LB}>Start Date</label>
            <input id="meta-start" type="date" className={IC} value={form.start_date ?? ''}
              onChange={(e) => { u({ start_date: e.target.value }); }} />
          </div>
          <div>
            <label htmlFor="meta-end" className={LB}>End Date</label>
            <input id="meta-end" type="date" className={IC} value={form.end_date ?? ''}
              onChange={(e) => { u({ end_date: e.target.value }); }}
              aria-invalid={dateError !== null}
              aria-describedby={dateError ? 'meta-date-err' : undefined} />
          </div>
        </div>
      )}
      {dateError && (
        <p id="meta-date-err" role="alert" className="text-xs text-destructive">{dateError}</p>
      )}
      {schema.showRenewal && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="meta-renewal" className={LB}>Auto-Renewal</label>
            <select id="meta-renewal" className={IC}
              value={form.auto_renewal ? 'yes' : 'no'}
              onChange={(e) => { u({ auto_renewal: e.target.value === 'yes' }); }}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
          <div>
            <label htmlFor="meta-notice" className={LB}>Notice Period (days)</label>
            <input id="meta-notice" type="number" min={0} className={IC}
              value={form.notice_period_days ?? ''}
              onChange={(e) => {
                u({ notice_period_days: e.target.value === '' ? undefined : Number(e.target.value) });
              }} />
          </div>
        </div>
      )}
      <div>
        <label htmlFor="meta-status" className={LB}>Status</label>
        <select id="meta-status" className={IC} value={form.status ?? contractStatus}
          onChange={(e) => { u({ status: e.target.value as ContractStatus }); }}>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="meta-notes" className={LB}>Notes</label>
        <textarea id="meta-notes" rows={2} className={`${IC} h-auto resize-none`}
          value={form.description ?? ''} onChange={(e) => { u({ description: e.target.value }); }} />
      </div>
      {saveError && <p className="text-xs text-destructive" role="alert">{saveError}</p>}
      <div className="flex gap-2 pt-1">
        <Button type="submit" size="sm" disabled={isSaving || dateError !== null} aria-label="Save contract changes">
          <Save className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={isSaving} aria-label="Cancel editing">
          <X className="h-3.5 w-3.5 mr-1" aria-hidden="true" />Cancel
        </Button>
      </div>
    </form>
  );
}
